import React, { useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TASK_COLORS = {
  referral: "#10b981",
};

export default function ReferralTaskModal({ task, referralToken, onClose }) {
  const [copied, setCopied] = useState(false);

  const pageName = window.location.pathname.split("/").filter(Boolean)[0] || "GiveawayCopy";
  const referralUrl = `${window.location.origin}/${pageName}?ref=${referralToken}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Mom3ntum - הזמן חברים וקבלו נקודות",
          text: "הצטרפו למאבק הקהילתי ובחרו בעצמכם את הכרטיסים!",
          url: referralUrl,
        });
      } catch (err) {
        console.log("Share cancelled or failed:", err);
      }
    }
  };

  const color = TASK_COLORS.referral || "#10b981";

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          key="modal"
          initial={{ y: 60, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0, scale: 0.97 }}
          transition={{ type: "spring", damping: 22, stiffness: 280 }}
          className="w-full max-w-md rounded-3xl overflow-hidden"
          style={{ background: "#111111", border: `1px solid ${color}30` }}
        >
          <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />

          <div className="p-6 pb-4" style={{ borderBottom: "1px solid #1a1a1a" }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🔗</span>
                <h2 className="font-black text-white text-lg leading-tight" dir="rtl">
                  {task.title}
                </h2>
              </div>
              <button onClick={onClose} className="p-1 flex-shrink-0 hover:opacity-70 transition-opacity">
                <X className="w-5 h-5" style={{ color: "rgba(255,255,255,0.4)" }} />
              </button>
            </div>
          </div>

          <div className="p-6" dir="rtl">
            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>
              {task.description}
            </p>

            {/* Referral count */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-xl p-3 mb-5 flex items-center justify-between"
              style={{ background: `${color}10`, border: `1px solid ${color}30` }}
            >
              <span className="text-sm font-bold" style={{ color: "rgba(255,255,255,0.5)" }}>
                חברים שהצטרפו
              </span>
              <span className="font-black text-2xl" style={{ color }}>
                {task.referral_count || 0}
              </span>
            </motion.div>

            {/* Points earned */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="rounded-xl p-3 mb-5 flex items-center justify-between"
              style={{ background: `${color}10`, border: `1px solid ${color}30` }}
            >
              <span className="text-sm font-bold" style={{ color: "rgba(255,255,255,0.5)" }}>
                נקודות שהרווחת
              </span>
              <span className="font-black text-2xl" style={{ color }}>
                +{task.points_earned || 0}
              </span>
            </motion.div>

            {/* Copy link section */}
            <div className="mb-4">
              <label className="block text-xs font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>
                קישור ההזמנה שלך
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralUrl}
                  readOnly
                  className="flex-1 rounded-xl px-4 py-3 text-xs font-medium outline-none"
                  style={{ background: "#1a1a1a", border: `1px solid #2a2a2a`, color: "#fff" }}
                />
                <button
                  onClick={handleCopy}
                  className="px-4 py-3 rounded-xl font-black text-xs transition-all flex items-center gap-2"
                  style={{ background: color, color: "#000" }}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" /> הוקל
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> העתק
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Share button */}
            {navigator.share && (
              <motion.button
                onClick={handleShare}
                whileTap={{ scale: 0.96 }}
                className="w-full py-4 rounded-xl font-black text-base transition-all"
                style={{ background: color, color: "#000" }}
              >
                📤 שתף עם חברים
              </motion.button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}